import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle2, Mic2, Building2, Briefcase, AlertCircle } from 'lucide-react';
export function SignupPage() {
  const [role, setRole] = useState<'artist' | 'venue' | 'agent' | null>(null);
  const [status, setStatus] = useState<'idle' | 'error'>('idle');
  return <div className="min-h-screen bg-background flex" data-id="element-5799">
      {/* Left Column - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-16 lg:px-24 py-12" data-id="element-5800">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="w-full max-w-md mx-auto" data-id="element-5801">
          <Link to="/" className="flex items-center gap-2 mb-8" data-id="element-5802">
            <span className="text-2xl font-bold tracking-tight text-textMain" data-id="element-5803">
              Booker
            </span>
            <div className="w-2 h-2 rounded-full bg-primary mt-1" data-id="element-5804"></div>
          </Link>

          <h1 className="text-3xl font-bold text-textMain mb-2" data-id="element-5805">
            Create your account
          </h1>
          <p className="text-textMuted text-sm mb-8" data-id="element-5806">
            Join Booker to streamline your live performance workflow.
          </p>

          {status === 'error' && <div className="mb-6 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3" data-id="element-5807">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" data-id="element-5808" />
              <p className="text-sm text-red-500" data-id="element-5809">
                This email is already in use. Please log in instead.
              </p>
            </div>}

          <div className="mb-6" data-id="element-5810">
            <label className="block text-sm font-medium text-textMuted mb-3" data-id="element-5811">
              I am a...
            </label>
            <div className="grid grid-cols-3 gap-3" data-id="element-5812">
              <button onClick={() => setRole('artist')} className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition-colors ${role === 'artist' ? 'bg-primary/10 border-primary text-primary' : 'bg-surface border-border text-textMuted hover:border-primary/50'}`} data-id="element-5813">
                <Mic2 className="w-6 h-6" data-id="element-5814" />
                <span className="text-xs font-medium text-center" data-id="element-5815">
                  Artist / Band
                </span>
              </button>
              <button onClick={() => setRole('venue')} className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition-colors ${role === 'venue' ? 'bg-primary/10 border-primary text-primary' : 'bg-surface border-border text-textMuted hover:border-primary/50'}`} data-id="element-5816">
                <Building2 className="w-6 h-6" data-id="element-5817" />
                <span className="text-xs font-medium text-center" data-id="element-5818">
                  Venue / Promoter
                </span>
              </button>
              <button onClick={() => setRole('agent')} className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition-colors ${role === 'agent' ? 'bg-primary/10 border-primary text-primary' : 'bg-surface border-border text-textMuted hover:border-primary/50'}`} data-id="element-5819">
                <Briefcase className="w-6 h-6" data-id="element-5820" />
                <span className="text-xs font-medium text-center" data-id="element-5821">
                  Agent / Manager
                </span>
              </button>
            </div>
          </div>

          <form className="space-y-4 mb-6" onSubmit={e => e.preventDefault()} data-id="element-5822">
            <div data-id="element-5823">
              <label className="block text-sm font-medium text-textMuted mb-1.5" data-id="element-5824">
                Full Name
              </label>
              <input type="text" className="w-full px-4 py-2.5 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:border-primary transition-colors" placeholder="Jane Doe" data-id="element-5825" />
            </div>
            <div data-id="element-5826">
              <label className="block text-sm font-medium text-textMuted mb-1.5" data-id="element-5827">
                Email
              </label>
              <input type="email" className="w-full px-4 py-2.5 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:border-primary transition-colors" placeholder="you@example.com" data-id="element-5828" />
            </div>
            <div data-id="element-5829">
              <label className="block text-sm font-medium text-textMuted mb-1.5" data-id="element-5830">
                Password
              </label>
              <input type="password" className="w-full px-4 py-2.5 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:border-primary transition-colors" placeholder="••••••••" data-id="element-5831" />
            </div>
            <div data-id="element-5832">
              <label className="block text-sm font-medium text-textMuted mb-1.5" data-id="element-5833">
                What best describes you? (Optional)
              </label>
              <select className="w-full px-4 py-2.5 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:border-primary transition-colors appearance-none text-textMain" data-id="element-5834">
                <option value="" data-id="element-5835">Select an option...</option>
                <option value="touring" data-id="element-5836">Touring Artist</option>
                <option value="local" data-id="element-5837">Local Act</option>
                <option value="indie_venue" data-id="element-5838">Independent Venue</option>
                <option value="festival" data-id="element-5839">Festival Organizer</option>
                <option value="boutique_agency" data-id="element-5840">Boutique Agency</option>
              </select>
            </div>

            <label className="flex items-start gap-3 mt-4" data-id="element-5841">
              <input type="checkbox" className="mt-1 rounded border-border bg-surface text-primary focus:ring-primary" data-id="element-5842" />
              <span className="text-xs text-textMuted leading-relaxed" data-id="element-5843">
                I agree to the{' '}
                <Link to="/terms" className="text-primary hover:underline" data-id="element-5844">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-primary hover:underline" data-id="element-5845">
                  Privacy Policy
                </Link>
                .
              </span>
            </label>

            <button className="w-full py-2.5 mt-2 bg-primary text-background rounded-lg font-medium hover:bg-primaryHover transition-colors shadow-[0_0_15px_rgba(245,158,11,0.2)]" data-id="element-5846">
              Create account
            </button>
          </form>

          <div className="relative mb-6" data-id="element-5847">
            <div className="absolute inset-0 flex items-center" data-id="element-5848">
              <div className="w-full border-t border-border" data-id="element-5849"></div>
            </div>
            <div className="relative flex justify-center text-xs" data-id="element-5850">
              <span className="bg-background px-2 text-textMuted" data-id="element-5851">
                Or sign up with
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-8" data-id="element-5852">
            <button className="py-2.5 bg-surface border border-border rounded-lg text-sm font-medium hover:bg-surfaceHover transition-colors flex items-center justify-center gap-2" data-id="element-5853">
              <svg className="w-4 h-4" viewBox="0 0 24 24" data-id="element-5854">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" data-id="element-5855" />
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" data-id="element-5856" />
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" data-id="element-5857" />
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" data-id="element-5858" />
              </svg>
              Google
            </button>
            <button className="py-2.5 bg-surface border border-border rounded-lg text-sm font-medium hover:bg-surfaceHover transition-colors flex items-center justify-center gap-2" data-id="element-5859">
              <svg className="w-4 h-4" viewBox="0 0 24 24" data-id="element-5860">
                <path fill="currentColor" d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zm12.6 0H12.6V0H24v11.4z" data-id="element-5861" />
              </svg>
              Microsoft
            </button>
          </div>

          <p className="text-center text-sm text-textMuted" data-id="element-5862">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:text-primaryHover font-medium transition-colors" data-id="element-5863">
              Log in
            </Link>
          </p>
        </motion.div>
      </div>

      {/* Right Column - Benefits */}
      <div className="hidden lg:flex flex-1 bg-surface border-l border-border items-center justify-center p-12" data-id="element-5864">
        <motion.div initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.2
      }} className="max-w-md" data-id="element-5865">
          <h2 className="text-2xl font-bold mb-8" data-id="element-5866">
            Everything you need to run live performance.
          </h2>

          <div className="space-y-6" data-id="element-5867">
            {[{
            title: 'Discovery & Routing',
            desc: 'Find venues, artists, and markets that actually fit.'
          }, {
            title: 'Deals & Negotiation',
            desc: 'Track every conversation, hold, offer, and counter in one place.'
          }, {
            title: 'Contracts & Payments',
            desc: 'Generate docs, collect signatures, request deposits, and monitor settlement.'
          }, {
            title: 'Lineup & Tour Ops',
            desc: 'Make sure the right people can actually play the show.'
          }, {
            title: 'Analytics & Ops',
            desc: 'Understand your route economics and team performance.'
          }].map((benefit, i) => <div key={i} className="flex gap-4" data-id="element-5868">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0 mt-0.5" data-id="element-5869">
                  <CheckCircle2 className="w-4 h-4 text-primary" data-id="element-5870" />
                </div>
                <div data-id="element-5871">
                  <h3 className="font-semibold text-sm mb-1" data-id="element-5872">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-textMuted" data-id="element-5873">{benefit.desc}</p>
                </div>
              </div>)}
          </div>
        </motion.div>
      </div>
    </div>;
}